import { Box, Button, Chip, Grid, Typography } from "@mui/material";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useProductsContext } from "../../Contexts/SidebarContext";
import { NewMessageDialog } from "./NewMessageDialog";
import { NetworkManager } from "../../Components/NetworkManager";
import { CommonUtil, readJson } from "../../utils/CommonUtil";
import ToastManager from "../../Components/ToastManager";
import SearchBarWithButton from "../BroadcastFBManage/DMComponents/SearchBarWithButton";
import MessageList from "../BroadcastFBManage/DMComponents/MessageList";
import MessageTabs from "../BroadcastFBManage/DMComponents/MessageTabs";
import { Messages } from "../BroadcastFBManage/DMComponents/types";

interface MarketingMessagesProps {
  handleBackToCampaigns: () => void;
}

const MarketingMessages: React.FC<MarketingMessagesProps> = ({
  handleBackToCampaigns,
}) => {
  const { sideBar } = useProductsContext();

  const [searchTerm, setSearchTerm] = useState("");
  const [editMessage, setEditMessage] = useState<any>(null);
  const [openMessageDialog, setOpenMessageDialog] = useState<any>(false);
  const [messages, setMessages] = useState<Messages[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);

  const {
    facebook: { pageId },
  } = readJson("fbbPageDetail") || "{}";
  const { adAccountId } = readJson("fbbAdAccountDetail") || "{}";
  const { messageCampaignId, messageCampaignName } =
    readJson("selectedCampaign") || {};

  const fetchMarketingMessages = useCallback(() => {
    if (!pageId || !adAccountId || !messageCampaignId) {
      return;
    }
    const pageDataReq = `pageId=${pageId}&adAccountId=${adAccountId}&messageCampaignId=${messageCampaignId}`;

    NetworkManager.sendJsonRequest(
      "broadcastMessageTemplates/getBroadcastTemplates",
      "",
      pageDataReq,
      "GET",
      true,
      onSuccessCall,
      onErrorCall,
    );
  }, []);

  const onSuccessCall = useCallback((data: any) => {
    const respData = CommonUtil.parseData(data);
    if (respData.statusCode === "BM0000") {
      const messagesList = respData?.broadcastMessageTemplateDetails;
      setMessages(messagesList);
    } else if (respData.statusCode !== "BM0000") {
      ToastManager.showToast("No messages are available.", "Error");
    }
  }, []);

  const onErrorCall = useCallback(() => {
    ToastManager.showToast("Failed to fetch messages.", "Error");
  }, []);

  useEffect(() => {
    fetchMarketingMessages();
  }, [fetchMarketingMessages]);

  const scheduledMessages = useMemo(
    () =>
      messages.filter((message: any) => message.templateStatus === "Not Sent"),
    [messages],
  );

  const filteredMessages = useMemo(
    () =>
      scheduledMessages.filter((message) =>
        message.messageTemplateName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()),
      ),
    [scheduledMessages, searchTerm],
  );

  const pastMessages = useMemo(
    () => messages.filter((message: any) => message.templateStatus === "Sent"),
    [messages],
  );

  const filteredPastMessages = useMemo(
    () =>
      pastMessages.filter((message) =>
        message.messageTemplateName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()),
      ),
    [pastMessages, searchTerm],
  );

  const handleOpenMessageDialog = useCallback(() => {
    setOpenMessageDialog(true);
    setEditMessage(null);
  }, []);

  const onEditSuccess = useCallback((response: any, data: any) => {
    const respData = CommonUtil.parseData(response);
    if (respData.statusCode === "BM0000") {
      const templateDetails = respData.templateData.template;
      const audienceList = respData.audienceList || data.audienceList || {};
      const excludedCampaignIds = respData.excludedCampaignIds || [];
      localStorage.setItem(
        "selectedMessageDetails",
        JSON.stringify(templateDetails),
      );
      setEditMessage({
        ...data,
        templateDetails,
        audienceList,
        excludedCampaignIds,
      });
      setOpenMessageDialog(true);
    } else {
      ToastManager.showToast(
        respData.Message || "Failed to fetch message details.",
        "Error",
      );
    }
  }, []);

  const onEditError = useCallback((_err: any) => {
    ToastManager.showToast("Error fetching message details", "Error");
  }, []);

  const handleEditMessage = useCallback(
    (data: any) => {
      localStorage.setItem("selectedMessage", JSON.stringify(data));
      const messageDetails = JSON.parse(
        localStorage.getItem("selectedMessage") || "{}",
      );
      const messageTemplateId = messageDetails?.messageTemplateId;
      const params = `messageTemplateId=${messageTemplateId}`;

      NetworkManager.sendJsonRequest(
        "broadcastMessageTemplates/getBroadcastTemplateData",
        "",
        params,
        "GET",
        true,
        (response: any) => onEditSuccess(response, data),
        onEditError,
      );
    },
    [onEditSuccess, onEditError],
  );

  const handleSaveMessage = useCallback((newMessage: any) => {
    setMessages((prevMessages) => {
      const existsMessage = prevMessages.some(
        (message: any) =>
          message.messageTemplateId === newMessage.messageTemplateId,
      );
      if (existsMessage) {
        return prevMessages.map((message: any) =>
          message.messageTemplateId === newMessage.messageTemplateId
            ? newMessage
            : message,
        );
      } else {
        return [...prevMessages, newMessage];
      }
    });
  }, []);

  const centeredContentSx = {
    width: "100%",
    maxWidth: { xs: "100%", md: "83.33%", lg: "66.66%" },
    mx: "auto",
  };

  return (
    <Box
      className="content-page"
      sx={{
        ml: sideBar ? "120px" : "120px",
        backgroundColor: "#fff",
        mr: 10,
      }}>
      <Box className="content">
        {/* ── Row 1: Back · Title · Campaign chip ── */}
        <Grid container spacing={2} sx={{ mt: 1, alignItems: "center" }}>
          <Grid size={4}>
            <Button
              variant="outlined"
              sx={{ textTransform: "none", ml: 12 }}
              onClick={handleBackToCampaigns}>
              ← Back
            </Button>
          </Grid>
          <Grid size={2}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontWeight: "bold", textAlign: "start", ml: 8 }}>
              Messages
            </Typography>
          </Grid>
          <Grid size={4}>
            <Typography
              gutterBottom
              sx={{ fontWeight: "500", textAlign: "end" }}>
              <Chip
                label={messageCampaignName}
                color="primary"
                variant="outlined"
              />
            </Typography>
          </Grid>
        </Grid>

        {/* ── Row 2: Search bar + New Message button ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            mt: 2,
          }}>
          <Box sx={centeredContentSx}>
            <SearchBarWithButton
              placeholder="Search Message..."
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onNew={handleOpenMessageDialog}
              newBtnLabel="+ New Message"
            />
          </Box>
        </Box>

        {/* ── Row 3: Tabs ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            mt: 1,
            mb: 2,
          }}>
          <Box sx={centeredContentSx}>
            <MessageTabs
              value={selectedTab}
              onChange={(_e, val) => setSelectedTab(val)}
            />
          </Box>
        </Box>
        {selectedTab === 0 && (
          <MessageList
            messages={filteredMessages}
            onEdit={handleEditMessage}
            emptyMessage="No scheduled messages"
          />
        )}
        {selectedTab === 1 && (
          <MessageList
            messages={filteredPastMessages}
            onEdit={undefined}
            emptyMessage="No past messages"
          />
        )}
      </Box>

      <NewMessageDialog
        open={openMessageDialog}
        onClose={() => setOpenMessageDialog(false)}
        onSave={handleSaveMessage}
        editMessage={editMessage}
        fetchMarketingMessages={fetchMarketingMessages}
      />
    </Box>
  );
};
export default MarketingMessages;
